import { useEffect, useState } from "react";
import { Loader2, BarChart3, Users, CreditCard, TrendingDown, Activity } from "lucide-react";
import api from "../lib/apiClient";

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-[#18181E] p-5" data-testid={testid}>
      <p className="text-xs text-zinc-500">{label}</p>
      <p className="font-display text-2xl font-extrabold text-zinc-100 mt-1">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="space-y-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-zinc-300"><Icon className="w-4 h-4 text-amber-400" /> {title}</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setD(r.data)).catch(() => setD(false)); }, []);

  if (d === null) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;
  if (d === false) return <p className="text-zinc-400" data-testid="analytics-error">Impossible de charger les analytics.</p>;

  const a = d.acquisition, c = d.conversion, co = d.checkout, r = d.retention, u = d.usage;
  return (
    <div className="space-y-8 max-w-6xl" data-testid="admin-analytics-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><BarChart3 className="w-7 h-7 text-amber-400" /> Analytics — Soft Launch</h1>
        <p className="text-zinc-500 text-sm mt-1">Suivi des métriques clés du lancement. {d.free_mode && <span className="text-emerald-400">Mode gratuit actif (ENABLE_PAID=false).</span>}</p>
      </div>

      <Section icon={Users} title="Acquisition & activation">
        <Stat label="Utilisateurs total" value={a.total_users} testid="an-total-users" />
        <Stat label="Inscriptions 7j" value={a.signups_7d} sub={`${a.signups_30d} sur 30j`} testid="an-signups" />
        <Stat label="Onboarding terminé" value={`${a.onboard_rate}%`} sub={`${a.onboarded} comptes`} testid="an-onboarding" />
        <Stat label="Actifs (14j)" value={a.active_14d} sub={`${a.verified} vérifiés`} testid="an-active" />
      </Section>

      <Section icon={CreditCard} title="Conversion payante">
        <Stat label="Premium" value={c.premium} sub="19,90€/mois" testid="an-premium" />
        <Stat label="Pro" value={c.pro} sub="49,90€/mois" testid="an-pro" />
        <Stat label="Taux de conversion" value={`${c.conversion_rate}%`} sub={`${c.paid_total} payants`} testid="an-conv-rate" />
        <Stat label="Managers actifs" value={c.managers_active} sub="offre gratuite" testid="an-managers" />
      </Section>

      <Section icon={Activity} title="Checkout">
        <Stat label="Sessions checkout" value={co.sessions} testid="an-co-sessions" />
        <Stat label="Payées" value={co.paid} testid="an-co-paid" />
        <Stat label="Abandonnées" value={co.abandoned} testid="an-co-abandoned" />
        <Stat label="Taux d'abandon" value={`${co.abandon_rate}%`} testid="an-co-abandon-rate" />
      </Section>

      <Section icon={TrendingDown} title="Rétention / Churn">
        <Stat label="Résiliés" value={r.cancelled} testid="an-cancelled" />
        <Stat label="Impayés (past due)" value={r.past_due} testid="an-past-due" />
        <Stat label="Taux de churn" value={`${r.churn_rate}%`} testid="an-churn" />
        <Stat label="Missions validées" value={u.tasks_done} testid="an-tasks-done" />
      </Section>

      <Section icon={BarChart3} title="Usage produit">
        <Stat label="Appels IA" value={u.ai_calls} testid="an-ai" />
        <Stat label="Projets créés" value={u.projects_created} testid="an-projects" />
        <Stat label="Demandes marketplace" value={u.requests_total} sub={`${u.proposals_total} propositions`} testid="an-requests" />
        <Stat label="Commandes payées" value={u.orders_paid} sub={`${u.pros_total} pros`} testid="an-orders" />
      </Section>
    </div>
  );
}
